// Pure level math. M2.4 design doc, §5.
//
//   XP required to *reach* level N (cumulative) = N² · 50
//
// So L1 needs 50 XP, L2 needs 200, L3 needs 450, L4 needs 800, L5 needs 1250...
// At ~200 XP/week (the design-doc target), Ryan reaches L3 in ~2 weeks and L5
// in ~6 weeks. After that, levels meaningfully stretch — L8 needs 3200 XP.
//
// Named tiers are intentionally non-cute. No mages, no ranks of guitar god.
// They read like a craft progression because that's what practice is.

export interface LevelInfo {
  /** 0-indexed when xp < XP_FOR_LEVEL[1] (i.e. before reaching L1). */
  level: number;
  /** Human-facing label for this level. */
  name: string;
  /** Total XP needed to *reach* this level. */
  xpForThisLevel: number;
  /** Total XP needed to *reach* the next level. */
  xpForNextLevel: number;
  /** XP earned inside the current level band. */
  xpIntoLevel: number;
  /** XP remaining to the next level. */
  xpToNext: number;
  /** Fraction of progress through the current level, 0..1. */
  progress: number;
}

// Quadratic curve: level N needs N² · 50 cumulative XP to reach.
export const XP_PER_LEVEL_COEFFICIENT = 50;

export function xpRequiredForLevel(level: number): number {
  if (level <= 0) return 0;
  return level * level * XP_PER_LEVEL_COEFFICIENT;
}

// Names are deliberately understated. Beginner → established craftsperson.
// "Maestro" only at L8+ because the curve makes it real work.
const LEVEL_NAMES: readonly string[] = [
  "Beginner", // L0 — pre-first-level
  "Apprentice", // L1
  "Student", // L2
  "Journeyman", // L3
  "Adept", // L4
  "Artisan", // L5
  "Craftsperson", // L6
  "Virtuoso", // L7
  "Maestro", // L8+
];

export function levelName(level: number): string {
  if (level <= 0) return LEVEL_NAMES[0];
  if (level >= LEVEL_NAMES.length) return LEVEL_NAMES[LEVEL_NAMES.length - 1];
  return LEVEL_NAMES[level];
}

export function levelFromXp(xp: number): LevelInfo {
  const safeXp = Math.max(0, Math.floor(xp));
  // Solve N² · 50 ≤ safeXp for largest integer N.
  // level = floor(sqrt(xp / 50))
  const level = Math.floor(Math.sqrt(safeXp / XP_PER_LEVEL_COEFFICIENT));
  const xpForThisLevel = xpRequiredForLevel(level);
  const xpForNextLevel = xpRequiredForLevel(level + 1);
  const bandSize = Math.max(1, xpForNextLevel - xpForThisLevel);
  const xpIntoLevel = safeXp - xpForThisLevel;
  const xpToNext = Math.max(0, xpForNextLevel - safeXp);
  const progress = Math.min(1, xpIntoLevel / bandSize);
  return {
    level,
    name: levelName(level),
    xpForThisLevel,
    xpForNextLevel,
    xpIntoLevel,
    xpToNext,
    progress,
  };
}

/** Format a number with thousands separators. 1240 -> "1,240". */
export function formatXp(xp: number): string {
  return Math.max(0, Math.floor(xp)).toLocaleString("en-US");
}

/** Roman-numeral level for display ("L IV"). Falls back to "L N" past 8. */
const ROMAN: readonly string[] = [
  "0",
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
];

export function levelRoman(level: number): string {
  if (level <= 0) return ROMAN[0];
  if (level < ROMAN.length) return ROMAN[level];
  return String(level);
}
